import { ApiError, ApiErrorResponse } from '../types';

/**
 * HTTP Client utility for API requests
 */
export class HttpClient {
  constructor(private baseURL: string) {}

  /**
   * Handle API error responses and throw appropriate ApiError
   */
  private async handleApiError(response: Response): Promise<never> {
    let errorResponse: ApiErrorResponse;
    
    try {
      errorResponse = await response.json();
    } catch {
      // Fallback for non-JSON responses
      errorResponse = { 
        detail: {
          detail: response.statusText || 'Request failed',
          error_type: 'http_error',
          timestamp: new Date().toISOString()
        }
      };
    }

    const errorMessage = errorResponse.detail.detail;
    throw new ApiError(errorMessage, response.status, errorResponse);
  }

  /**
   * Handle network and generic errors
   */
  private handleGenericError(error: unknown, operation: string): never {
    if (error instanceof ApiError) {
      throw error;
    }
    
    console.error(`${operation} failed:`, error);
    
    if (error instanceof TypeError) {
      // TypeError from fetch can indicate server errors (500, CORS, etc.)
      // Treat as API error rather than network error
      throw new ApiError('Server error occurred', 500);
    }
    
    throw new ApiError(
      `${operation} failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      0
    );
  }

  /**
   * Get authorization headers including access code if available
   */
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    
    // Add access code header if available in localStorage
    const accessCode = localStorage.getItem('auth_access_code');
    if (accessCode) {
      headers['X-Access-Code'] = accessCode;
    }
    
    return headers;
  }

  /**
   * Make HTTP request with error handling
   */
  async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        await this.handleApiError(response);
      }

      return response.json();
    } catch (error) {
      this.handleGenericError(error, 'Request');
    }
  }

  /**
   * Make HTTP request for blob data
   */
  async requestBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        await this.handleApiError(response);
      }

      return response.blob();
    } catch (error) {
      this.handleGenericError(error, 'Blob request');
    }
  }

  /**
   * Upload single file with FormData
   */
  async uploadFile<T>(
    endpoint: string, 
    file: File, 
    additionalData: Record<string, string> = {}
  ): Promise<T> {
    try {
      const formData = new FormData();
      
      formData.append('file', file);
      
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders(),
        },
        body: formData,
      });

      if (!response.ok) {
        await this.handleApiError(response);
      }

      return response.json();
    } catch (error) {
      this.handleGenericError(error, 'File upload');
    }
  }
}